import { useState, useCallback } from 'react';
import { Layout, Database, GitBranch, MousePointerClick, ClipboardList, Pencil, MessageCircle, Target, ChevronDown, X, Plus } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { FOREST_GREEN, MID_GRAY, ERROR, WARNING, BORDER, SHADOWS, WHITE } from '@/styles/tokens';
import { usePlanStore } from '@/stores/ai/plan-store';
import { PlanAuditResultCard } from './PlanAuditResultCard';
import { PLAN_DOMAIN_ICONS, PLAN_DOMAIN_LABELS } from '@/types/plan';
import type { Roadmap, AuditResult, PlanSectionDomain, RoadmapPhase } from '@/types/plan';
import type { LucideIcon } from 'lucide-react';

function verdictColor(verdict: 'pass' | 'warn' | 'fail'): string {
  switch (verdict) {
    case 'pass': return FOREST_GREEN.DEFAULT;
    case 'warn': return WARNING.DEFAULT;
    case 'fail': return ERROR.DEFAULT;
  }
}

const DOMAIN_ICON_COMPONENTS: Record<string, LucideIcon> = { Layout, Database, GitBranch, MousePointerClick };

function getDomainIcon(domain: PlanSectionDomain): LucideIcon {
  const iconName = PLAN_DOMAIN_ICONS[domain];
  return DOMAIN_ICON_COMPONENTS[iconName] ?? Layout;
}

const COMPLEXITY_COLORS: Record<string, string> = {
  low: FOREST_GREEN.DEFAULT,
  medium: WARNING.DEFAULT,
  high: ERROR.DEFAULT,
};

interface PlanRoadmapViewProps {
  roadmap: Roadmap;
  auditResult?: AuditResult;
  onContinue: () => void;
  onRefineWithChat: () => void;
  onModifyPhase: (phaseId: string) => void;
  onDiscard: () => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Editable list sub-component (used for tasks and acceptance criteria)
// ─────────────────────────────────────────────────────────────────────────────

function EditableList({
  items,
  onChange,
  placeholder,
}: {
  items: string[];
  onChange: (items: string[]) => void;
  placeholder: string;
}) {
  return (
    <div style={{ margin: '4px 0', paddingLeft: 16 }}>
      {items.map((item, i) => (
        <div key={i} className="flex items-center gap-1" style={{ marginBottom: 2 }}>
          <span style={{ fontSize: 12, color: MID_GRAY.lighter }}>&#8226;</span>
          <input
            type="text"
            value={item}
            onChange={(e) => {
              const updated = [...items];
              updated[i] = e.target.value;
              onChange(updated);
            }}
            placeholder={placeholder}
            style={{
              flex: 1,
              fontSize: 12,
              padding: '2px 4px',
              border: `1px solid ${BORDER.DEFAULT}`,
              borderRadius: 3,
              outline: 'none',
              lineHeight: 1.5,
            }}
          />
          <button
            type="button"
            onClick={() => onChange(items.filter((_, idx) => idx !== i))}
            title="Remove"
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex' }}
          >
            <X style={{ width: 12, height: 12, color: MID_GRAY.lighter }} />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => onChange([...items, ''])}
        className="flex items-center gap-1"
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          fontSize: 11,
          color: FOREST_GREEN.DEFAULT,
          padding: '2px 0',
        }}
      >
        <Plus style={{ width: 11, height: 11 }} />
        Add {placeholder.toLowerCase()}
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────

export function PlanRoadmapView({ roadmap, auditResult, onContinue, onRefineWithChat, onModifyPhase, onDiscard }: PlanRoadmapViewProps) {
  const setPlanName = usePlanStore((s) => s.setPlanName);
  const updateRoadmapPhase = usePlanStore((s) => s.updateRoadmapPhase);
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState(roadmap.title);
  const [showCostConfirm, setShowCostConfirm] = useState(false);
  const [isInlineEditing, setIsInlineEditing] = useState(false);
  const [editingPhaseTitle, setEditingPhaseTitle] = useState<string | null>(null);

  const avgSec = 10;
  const mins = (roadmap.estimatedLLMCalls ?? 0) * avgSec / 60;
  const minTime = Math.max(1, Math.round(mins * 0.7));
  const maxTime = Math.round(mins * 1.3);
  const estimatedCost = roadmap.estimatedCost ?? 0;
  const needsCostConsent = estimatedCost > 1.0;

  const handleNameSubmit = useCallback(() => {
    setEditingName(false);
    setPlanName(nameValue.trim() || roadmap.title);
  }, [nameValue, roadmap.title, setPlanName]);

  const handleContinue = useCallback(() => {
    if (needsCostConsent && !showCostConfirm) {
      setShowCostConfirm(true);
      return;
    }
    setShowCostConfirm(false);
    onContinue();
  }, [needsCostConsent, showCostConfirm, onContinue]);

  return (
    <div
      style={{
        border: `1px solid ${BORDER.DEFAULT}`,
        borderRadius: 8,
        background: WHITE,
        boxShadow: SHADOWS.sm,
        padding: 16,
      }}
    >
      {/* Header */}
      <div style={{ marginBottom: 12 }}>
        <div className="flex items-center gap-2" style={{ marginBottom: 4 }}>
          <ClipboardList className="w-4 h-4" style={{ color: FOREST_GREEN.DEFAULT }} />
          <span style={{ fontSize: 13, fontWeight: 600, color: MID_GRAY.DEFAULT }}>Roadmap:</span>
          {editingName ? (
            <input
              type="text"
              value={nameValue}
              onChange={(e) => setNameValue(e.target.value)}
              onBlur={handleNameSubmit}
              onKeyDown={(e) => { if (e.key === 'Enter') handleNameSubmit(); }}
              autoFocus
              aria-label="Edit plan name"
              style={{
                flex: 1,
                fontSize: 14,
                fontWeight: 600,
                border: `1px solid ${BORDER.DEFAULT}`,
                borderRadius: 4,
                padding: '2px 6px',
                outline: 'none',
              }}
            />
          ) : (
            <button
              type="button"
              onClick={() => setEditingName(true)}
              style={{
                flex: 1,
                fontSize: 14,
                fontWeight: 600,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                textAlign: 'left',
                padding: '2px 6px',
                borderRadius: 4,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = `${MID_GRAY.DEFAULT}08`; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
            >
              {nameValue}
            </button>
          )}
        </div>
        <div style={{ fontSize: 12, color: MID_GRAY.DEFAULT, paddingLeft: 24 }}>
          {(roadmap.phases ?? []).length} phase{(roadmap.phases ?? []).length !== 1 ? 's' : ''}
          {' · '}~{roadmap.estimatedLLMCalls ?? 0} LLM calls
          {estimatedCost > 0 && ` · ~$${estimatedCost.toFixed(2)}`}
        </div>
        <div style={{ fontSize: 12, color: MID_GRAY.lighter, paddingLeft: 24 }}>
          ~{minTime}-{maxTime} min execution
        </div>
      </div>

      {/* Timeline */}
      <div style={{ position: 'relative', paddingLeft: 24, marginBottom: 16 }}>
        {/* Vertical line */}
        <div
          style={{
            position: 'absolute',
            left: 11,
            top: 16,
            bottom: 16,
            width: 2,
            background: BORDER.DEFAULT,
          }}
        />

        {(roadmap.phases ?? []).map((phase: RoadmapPhase, index: number) => {
          const DomainIcon = getDomainIcon(phase.domain);
          const complexityColor = COMPLEXITY_COLORS[phase.estimatedComplexity] ?? MID_GRAY.DEFAULT;
          const isEditingTitle = editingPhaseTitle === phase.id;

          return (
            <div
              key={phase.id}
              style={{
                position: 'relative',
                paddingBottom: index < (roadmap.phases ?? []).length - 1 ? 16 : 0,
              }}
            >
              {/* Numbered circle */}
              <div
                style={{
                  position: 'absolute',
                  left: -24,
                  top: 0,
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 11,
                  fontWeight: 700,
                  color: WHITE,
                  background: FOREST_GREEN.DEFAULT,
                  zIndex: 1,
                }}
              >
                {phase.phaseNumber}
              </div>

              {/* Phase card */}
              <div style={{ paddingLeft: 8 }}>
                <div className="flex items-center gap-2 flex-wrap" style={{ marginBottom: 4 }}>
                  {isInlineEditing && isEditingTitle ? (
                    <input
                      type="text"
                      defaultValue={phase.title}
                      autoFocus
                      onBlur={(e) => {
                        const val = e.target.value.trim();
                        if (val && val !== phase.title) updateRoadmapPhase(phase.id, { title: val });
                        setEditingPhaseTitle(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                        if (e.key === 'Escape') setEditingPhaseTitle(null);
                      }}
                      style={{
                        flex: 1,
                        fontWeight: 600,
                        fontSize: 13,
                        border: `1px solid ${BORDER.DEFAULT}`,
                        borderRadius: 3,
                        padding: '1px 4px',
                        outline: 'none',
                      }}
                    />
                  ) : (
                    <span
                      style={{
                        fontWeight: 600,
                        fontSize: 13,
                        cursor: isInlineEditing ? 'text' : undefined,
                        borderBottom: isInlineEditing ? `1px dashed ${BORDER.DEFAULT}` : undefined,
                      }}
                      onClick={isInlineEditing ? () => setEditingPhaseTitle(phase.id) : undefined}
                    >
                      Phase {phase.phaseNumber}: {phase.title}
                    </span>
                  )}

                  {/* Phase-level modify target (visible when NOT inline editing) */}
                  {!isInlineEditing && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          onClick={() => onModifyPhase(phase.id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 2,
                            marginLeft: 'auto',
                            display: 'flex',
                            opacity: 0.4,
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.4'; }}
                        >
                          <Target style={{ width: 14, height: 14, color: MID_GRAY.DEFAULT }} />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>Modify this phase with chat</TooltipContent>
                    </Tooltip>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-wrap" style={{ marginBottom: 6 }}>
                  <span
                    className="inline-flex items-center gap-1"
                    style={{
                      fontSize: 11,
                      padding: '1px 6px',
                      borderRadius: 10,
                      background: `${FOREST_GREEN.DEFAULT}08`,
                      color: FOREST_GREEN.DEFAULT,
                    }}
                  >
                    <DomainIcon style={{ width: 12, height: 12 }} />
                    {PLAN_DOMAIN_LABELS[phase.domain]}
                  </span>
                  <span
                    style={{
                      fontSize: 11,
                      padding: '1px 6px',
                      borderRadius: 10,
                      background: `${complexityColor}10`,
                      color: complexityColor,
                      fontWeight: 500,
                      textTransform: 'capitalize',
                    }}
                  >
                    {phase.estimatedComplexity} complexity
                  </span>
                </div>

                {/* Dependencies */}
                {(phase.dependsOn ?? []).length > 0 && (
                  <div style={{ fontSize: 11, color: MID_GRAY.lighter, marginBottom: 4 }}>
                    Depends on: {(phase.dependsOn ?? []).map((depId) => {
                      const dep = roadmap.phases.find((p) => p.id === depId);
                      return dep ? `Phase ${dep.phaseNumber}` : depId;
                    }).join(', ')}
                  </div>
                )}

                {/* Structured description */}
                {phase.description && phase.description.includes('## ') && (
                  <div style={{ fontSize: 12, color: MID_GRAY.DEFAULT, lineHeight: 1.6, whiteSpace: 'pre-wrap', marginBottom: 6 }}>
                    {phase.description}
                  </div>
                )}

                {/* Tasks */}
                {isInlineEditing ? (
                  <EditableList
                    items={phase.tasks ?? []}
                    onChange={(tasks) => updateRoadmapPhase(phase.id, { tasks })}
                    placeholder="Task"
                  />
                ) : (
                  (phase.tasks ?? []).length > 0 && (
                    <ul style={{ margin: '0 0 4px 0', paddingLeft: 16, fontSize: 12, color: MID_GRAY.DEFAULT }}>
                      {(phase.tasks ?? []).map((task, i) => (
                        <li key={i} style={{ lineHeight: 1.5 }}>
                          {typeof task === 'string' ? task : (task as any).description || JSON.stringify(task)}
                        </li>
                      ))}
                    </ul>
                  )
                )}

                {/* Acceptance criteria */}
                {isInlineEditing ? (
                  <EditableList
                    items={phase.acceptanceCriteria ?? []}
                    onChange={(criteria) => updateRoadmapPhase(phase.id, { acceptanceCriteria: criteria })}
                    placeholder="Criterion"
                  />
                ) : (
                  (phase.acceptanceCriteria ?? []).length > 0 && (
                    <div style={{ fontSize: 11, color: MID_GRAY.lighter }}>
                      Acceptance: {(phase.acceptanceCriteria ?? []).join('; ')}
                    </div>
                  )
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Audit indicator */}
      {auditResult && (
        <div style={{ marginBottom: 16 }}>
          <PlanAuditResultCard auditResult={auditResult} auditType="Roadmap Audit" compact />
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2 flex-wrap">
        <TooltipProvider>
          {needsCostConsent && showCostConfirm ? (
            <div
              className="flex items-center gap-2 flex-wrap"
              style={{
                padding: '8px 12px',
                borderRadius: 6,
                background: `${WARNING.DEFAULT}10`,
                border: `1px solid ${WARNING.DEFAULT}30`,
                fontSize: 12,
              }}
            >
              <span>This will use ~{roadmap.estimatedLLMCalls ?? 0} LLM calls (~${estimatedCost.toFixed(2)}). Continue?</span>
              <button
                type="button"
                onClick={onContinue}
                style={{
                  padding: '4px 12px',
                  borderRadius: 4,
                  border: 'none',
                  background: FOREST_GREEN.DEFAULT,
                  color: WHITE,
                  cursor: 'pointer',
                  fontSize: 12,
                  fontWeight: 500,
                }}
              >
                Yes, execute
              </button>
              <button
                type="button"
                onClick={() => setShowCostConfirm(false)}
                style={{
                  padding: '4px 12px',
                  borderRadius: 4,
                  border: `1px solid ${BORDER.DEFAULT}`,
                  background: 'transparent',
                  cursor: 'pointer',
                  fontSize: 12,
                }}
              >
                Cancel
              </button>
            </div>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={handleContinue}
                  style={{
                    padding: '8px 20px',
                    borderRadius: 6,
                    border: 'none',
                    background: FOREST_GREEN.DEFAULT,
                    color: WHITE,
                    cursor: 'pointer',
                    fontSize: 13,
                    fontWeight: 500,
                  }}
                >
                  Continue
                </button>
              </TooltipTrigger>
              {needsCostConsent && (
                <TooltipContent>Estimated cost: ~${estimatedCost.toFixed(2)}</TooltipContent>
              )}
            </Tooltip>
          )}
        </TooltipProvider>

        {isInlineEditing ? (
          <button
            type="button"
            onClick={() => {
              setIsInlineEditing(false);
              setEditingPhaseTitle(null);
            }}
            style={{
              padding: '8px 20px',
              borderRadius: 6,
              border: `1px solid ${FOREST_GREEN.DEFAULT}`,
              background: `${FOREST_GREEN.DEFAULT}08`,
              color: FOREST_GREEN.DEFAULT,
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 500,
            }}
          >
            Done Editing
          </button>
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                style={{
                  padding: '8px 20px',
                  borderRadius: 6,
                  border: `1px solid ${BORDER.DEFAULT}`,
                  background: 'transparent',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                Modify
                <ChevronDown style={{ width: 14, height: 14 }} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" sideOffset={4}>
              <DropdownMenuItem onSelect={() => setIsInlineEditing(true)}>
                <Pencil style={{ width: 14, height: 14, marginRight: 8 }} />
                Edit Inline
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={onRefineWithChat}>
                <MessageCircle style={{ width: 14, height: 14, marginRight: 8 }} />
                Refine with Chat
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        <button
          type="button"
          onClick={onDiscard}
          style={{
            padding: '8px 20px',
            borderRadius: 6,
            border: 'none',
            background: 'transparent',
            color: ERROR.DEFAULT,
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: 500,
          }}
        >
          Discard
        </button>
      </div>
    </div>
  );
}
